<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header hris-page-header-row">
        <div>
          <span class="hris-page-eyebrow">Payroll</span>
          <h1 class="hris-page-title">Employee Loan Applications</h1>
          <p class="hris-page-subtitle">Review and process loan requests from employees.</p>
        </div>
        <q-btn
          unelevated
          class="hris-q-outline"
          icon="fas fa-sync-alt"
          label="Refresh"
          @click="loadAdminList"
          :loading="loading"
        ></q-btn>
      </div>

      <div class="hris-toolbar">
        <q-select
          outlined
          dense
          color="green"
          v-model="adminStatus"
          :options="adminStatusOptions"
          label="Status Filter"
          emit-value
          map-options
          style="width: 200px"
          @input="loadAdminList"
        ></q-select>
        <q-input outlined dense color="green" debounce="250" v-model="searchAdmin" placeholder="Search" style="min-width: 220px">
          <template v-slot:append>
            <i class="fas fa-search"></i>
          </template>
        </q-input>
      </div>

      <div class="hris-panel">
        <q-table
          flat
          :dense="$q.screen.lt.md"
          :grid="$q.screen.xs"
          title="Loan Applications"
          :data="adminRows"
          :columns="adminColumns"
          row-key="id"
          separator="cell"
          :pagination.sync="pagination"
          :loading="loading"
          :filter="searchAdmin"
        >
          <template v-slot:body="props">
            <q-tr :props="props">
              <q-td key="id" :props="props">{{ props.row.id }}</q-td>
              <q-td key="employee" :props="props">{{ formatEmployeeName(props.row) }}</q-td>
              <q-td key="loan_type" :props="props">{{ props.row.loan_type }}</q-td>
              <q-td key="requested_amount" :props="props">{{ formatMoney(props.row.requested_amount) }}</q-td>
              <q-td key="term_months" :props="props">{{ props.row.term_months }}</q-td>
              <q-td key="monthly_deduction" :props="props">{{ formatMoney(props.row.monthly_deduction) }}</q-td>
              <q-td key="start_date" :props="props">{{ props.row.start_date }}</q-td>
              <q-td key="status" :props="props">
                <span
                  class="hris-badge"
                  :class="(props.row.status || '').toUpperCase() === 'APPROVED' ? 'hris-badge-success' : (props.row.status || '').toUpperCase() === 'DENIED' ? 'hris-badge-danger' : 'hris-badge-warning'"
                >{{ (props.row.status || '').toUpperCase() }}</span>
              </q-td>
              <q-td key="action" :props="props">
                <div class="q-gutter-xs" v-if="(props.row.status || '').toUpperCase() === 'PENDING'">
                  <q-btn unelevated dense size="sm" class="hris-q-primary" icon="fas fa-check" label="Approve" @click="confirmApprove(props.row)"></q-btn>
                  <q-btn unelevated dense size="sm" color="negative" icon="fas fa-times" label="Deny" @click="confirmDeny(props.row)"></q-btn>
                </div>
                <div v-else>
                  {{ props.row.remarks || '-' }}
                </div>
              </q-td>
            </q-tr>
          </template>
        </q-table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MyApprovals_Loan',
  data() {
    return {
      loading: false,
      adminRows: [],
      adminStatus: 'PENDING',
      searchAdmin: '',
      adminStatusOptions: [
        { label: 'Pending', value: 'PENDING' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Denied', value: 'DENIED' }
      ],
      pagination: {
        rowsPerPage: 20
      },
      adminColumns: [
        { name: 'id', align: 'left', label: 'ID', field: 'id', sortable: true },
        { name: 'employee', align: 'left', label: 'Employee', field: 'employee', sortable: false },
        { name: 'loan_type', align: 'left', label: 'Loan Type', field: 'loan_type', sortable: true },
        { name: 'requested_amount', align: 'right', label: 'Amount Requested', field: 'requested_amount', sortable: true },
        { name: 'term_months', align: 'center', label: 'Term (Months)', field: 'term_months', sortable: true },
        { name: 'monthly_deduction', align: 'right', label: 'Monthly Deduction', field: 'monthly_deduction', sortable: true },
        { name: 'start_date', align: 'left', label: 'Start Date', field: 'start_date', sortable: true },
        { name: 'status', align: 'center', label: 'Status', field: 'status', sortable: true },
        { name: 'action', align: 'center', label: 'Action', field: 'action' }
      ]
    }
  },
  created() {
    this.loadAdminList();
  },
  methods: {
    apiClient() {
      return axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      });
    },
    formatMoney(val) {
      return parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    statusColor(status) {
      const s = (status || '').toUpperCase();
      if (s === 'APPROVED') return 'green';
      if (s === 'DENIED') return 'red';
      return 'orange';
    },
    formatEmployeeName(row) {
      return `${row.lastname}, ${row.firstname}`.replace(/\s+/g, ' ').trim();
    },
    async loadAdminList() {
      this.loading = true;
      try {
        const res = await this.apiClient().get('/payroll/deduction/admin/list', { params: { status: this.adminStatus } });
        this.adminRows = (res.data && res.data.data) ? res.data.data : [];
      } catch (e) {
        this.$q.notify({ color: 'red', message: 'Failed to load list.' });
      } finally {
        this.loading = false;
      }
    },
    confirmApprove(row) {
      this.$q.dialog({
        title: 'Approve Loan',
        message: 'Approve this loan and sync to payroll deductions?',
        prompt: { model: '', type: 'text', label: 'Remarks (Optional)' },
        ok: { label: 'Approve', color: 'green' },
        cancel: true,
        persistent: true
      }).onOk(async (remarks) => {
        this.processAction(row.id, 'approve', remarks);
      });
    },
    confirmDeny(row) {
      this.$q.dialog({
        title: 'Deny Loan',
        message: 'Deny this loan application?',
        prompt: { model: '', type: 'text', label: 'Remarks (Required)', isValid: val => !!val },
        ok: { label: 'Deny', color: 'red' },
        cancel: true,
        persistent: true
      }).onOk(async (remarks) => {
        this.processAction(row.id, 'deny', remarks);
      });
    },
    async processAction(id, action, remarks) {
      this.loading = true;
      try {
        const endpoint = `/payroll/deduction/admin/${id}/${action}`;
        const res = await this.apiClient().post(endpoint, { remarks });
        if (res.data.status) {
          this.$q.notify({ color: 'green', message: `Loan ${action}d successfully.` });
          this.loadAdminList();
        } else {
          this.$q.notify({ color: 'red', message: res.data.message || 'Action failed.' });
        }
      } catch (e) {
        this.$q.notify({ color: 'red', message: 'Error processing action.' });
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>
